import { useEffect, useState } from 'react';
import { DarwinCollapse } from 'components/darwin-ui';
import styles from '../Organizations.module.css';
import { CompanyAddress, Location } from 'types/organizationTypes';
import { useCreateOrganizationFormContext } from 'contexts/CreateOrganizationFormContext';

const { Panel } = DarwinCollapse;

type AddressBookAddressesCollapseProps = {
  addresses: CompanyAddress[];
  handleSetSelectedAddress: (address: CompanyAddress) => void;
  handleSetSelectedLocation: (location: Location) => void;
};

export const AddressBookAddressesCollapse = ({
  addresses,
  handleSetSelectedAddress,
  handleSetSelectedLocation,
}: AddressBookAddressesCollapseProps): JSX.Element => {
  const [activeKey, setActiveKey] = useState<string | string[] | null>('');
  const { setHasToResetCollapse, hasToResetCollapse } =
    useCreateOrganizationFormContext();
  const [activeLocationIndex, setActiveLocationIndex] = useState<number | null>(
    null
  );

  // clear selection after editing an address or location
  useEffect(() => {
    if (hasToResetCollapse) {
      setActiveKey(null);
      setActiveLocationIndex(null);
      handleSetSelectedAddress({} as CompanyAddress);
      handleSetSelectedLocation({} as Location);
      setHasToResetCollapse(false);
    }
  }, [
    handleSetSelectedAddress,
    handleSetSelectedLocation,
    hasToResetCollapse,
    setHasToResetCollapse,
  ]);

  const handlePanelChange = (key: string | string[]) => {
    if (!key || (Array.isArray(key) && key.length === 0)) {
      handleSetSelectedAddress({} as CompanyAddress);
      setActiveKey(key);
      return;
    }
    const selectedAddress = addresses[+key];
    setActiveKey(key);
    handleSetSelectedAddress(selectedAddress);
    handleSetSelectedLocation({} as Location);
    setActiveLocationIndex(null);
  };

  return (
    <DarwinCollapse
      className={`${styles.organizationFormAddressCollapse}`}
      accordion
      activeKey={activeKey as string[]}
      onChange={handlePanelChange}
    >
      {addresses.map((address, index) => (
        <Panel
          key={index.toString()}
          header={
            <div className={styles.addressCollapseTitle}>
              {address.addressName}
            </div>
          }
          className={`${styles.organizationFormAddressPanel} ${
            activeKey?.[0] === index.toString()
              ? styles.organizationFormActiveAddressPanel
              : ''
          }`}
        >
          {address.locations.map((loc, locIndex) => (
            <p
              onClick={() => {
                handleSetSelectedLocation(loc);
                setActiveLocationIndex(locIndex);
              }}
              className={`${styles.locationName} ${
                styles.organizationFormAddressPanel
              } ${styles.organizationFormLocation} ${
                activeLocationIndex === locIndex
                  ? styles.organizationFormActiveLocation
                  : ''
              }`}
              key={locIndex}
            >
              {loc.name}
            </p>
          ))}
        </Panel>
      ))}
    </DarwinCollapse>
  );
};
